import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, UserPlus, Trash2, KeyRound, ShieldCheck, ShieldOff, Users,
} from "lucide-react";

const ROLES = [
  { v: "salesman", label: "Salesman (mobile app)" },
  { v: "manager", label: "Manager (full access)" },
];

export default function UsersPanel({ meEmail }) {
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "salesman" });
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Email and password are needed"); return; }
    setBusy(true);
    try {
      await api.post("/auth/users", { ...form, name: form.name || form.email.split("@")[0] });
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "salesman" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const patch = async (u, body, msg) => {
    try {
      await api.patch(`/users/${u.id}`, body);
      toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPassword = (u) => { setPwUser(u); setPwValue(""); };
  const savePassword = () => {
    if (!pwValue || pwValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    patch(pwUser, { password: pwValue }, "Password updated");
    setPwUser(null); setPwValue("");
  };

  const remove = async (u) => {
    if (!(await confirm({ title: "Delete user", description: `Delete ${u.email}? This cannot be undone.`, confirmText: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A] flex items-center gap-2">
          <Users className="w-6 h-6" /> Team
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">Add salesmen or managers, reset passwords, switch access off.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <form onSubmit={create} className="lg:col-span-4 bg-white border border-[#E5E7EB] rounded-lg p-4 h-fit" data-testid="add-user-form">
          <h3 className="font-bold mb-3 flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#1E3A8A]" /> Add someone</h3>
          <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name"
            className="w-full h-11 px-3 mb-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
          <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Email</label>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email"
            className="w-full h-11 px-3 mb-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
          <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Password</label>
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password"
            className="w-full h-11 px-3 mb-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
          <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Access</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role"
            className="w-full h-11 px-3 mb-4 rounded-md border border-[#D1D5DB] bg-white focus:outline-none focus:border-[#1E3A8A]">
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
          <button type="submit" disabled={busy} data-testid="create-user-btn"
            className="w-full h-11 rounded-md bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create user
          </button>
          <p className="text-xs text-[#9CA3AF] mt-3">All API keys stay on the server — nobody, including your team, can see them from the app.</p>
        </form>

        <div className="lg:col-span-8 bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="users-table">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  {["User", "Access", "Visits", "Last login", ""].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A] mx-auto" /></td></tr>
                ) : users.map((u) => (
                  <tr key={u.id} className={`border-b border-[#F3F4F6] ${u.active === false ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-3">
                      <div className="font-bold text-[#111827] text-sm">{u.name || u.email}</div>
                      <div className="text-xs text-[#6B7280]">{u.email}{u.email === meEmail ? " · you" : ""}</div>
                      {u.active === false && <div className="text-xs font-bold text-[#B91C1C]">Access off</div>}
                    </td>
                    <td className="px-3 py-3">
                      <select value={u.role} onChange={(e) => patch(u, { role: e.target.value }, "Access updated")}
                        data-testid={`user-role-${u.id}`}
                        className="h-9 px-2 text-sm rounded-md border border-[#D1D5DB] bg-white">
                        <option value="salesman">Salesman</option>
                        <option value="manager">Manager</option>
                      </select>
                    </td>
                    <td className="px-3 py-3 text-sm font-bold text-[#111827]">{u.visits}</td>
                    <td className="px-3 py-3 text-sm text-[#6B7280]">{u.last_login ? new Date(u.last_login).toLocaleString() : "never"}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => resetPassword(u)} data-testid={`reset-pw-${u.id}`} title="Reset password"
                          className="p-2 rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"><KeyRound className="w-4 h-4" /></button>
                        <button onClick={() => patch(u, { active: u.active === false }, u.active === false ? "Access restored" : "Access switched off")}
                          data-testid={`toggle-active-${u.id}`} title={u.active === false ? "Enable" : "Disable"}
                          className="p-2 rounded-md text-[#6B7280] hover:bg-[#F3F4F6]">
                          {u.active === false ? <ShieldOff className="w-4 h-4 text-[#B91C1C]" /> : <ShieldCheck className="w-4 h-4 text-[#047857]" />}
                        </button>
                        <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} title="Delete" disabled={u.email === meEmail}
                          className="p-2 rounded-md text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEE2E2] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#9CA3AF]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setPwValue(""); } }}>
        <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="text-lg font-black text-[#1E3A8A]">Reset password</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280] -mt-2">New password for <b>{pwUser?.email}</b> (min 6 characters).</p>
          <input type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} data-testid="reset-pw-input"
            autoFocus onKeyDown={(e) => { if (e.key === "Enter") savePassword(); }}
            className="w-full h-11 px-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" placeholder="New password" />
          <div className="flex gap-2">
            <button onClick={() => { setPwUser(null); setPwValue(""); }} className="flex-1 h-11 rounded-md border border-[#D1D5DB] font-bold text-sm">Cancel</button>
            <button onClick={savePassword} data-testid="reset-pw-save" className="flex-1 h-11 rounded-md bg-[#1E3A8A] text-white font-bold text-sm">Save password</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
